import { useState } from 'react';

import { STREAMERS, type ScheduleItem } from '../data/streamers';
import type { ScheduleStatus } from '../hooks/useScheduleStatus';

import { StreamerDetailModal } from './StreamerDetailModal';
import styles from './StreamerScheduleCard.module.css';

interface StreamerScheduleCardProps {
  /** 카드에 그릴 일정 항목 (스트리머 또는 합방 피날레) */
  item: ScheduleItem;
  /** 현재 시각 기준 상태 — 뱃지 표시 분기 */
  status: ScheduleStatus;
}

/**
 * 한 줄짜리 방송 일정 카드.
 *
 * 디자인 시스템 HTML(`darakcast_kidsday_design_system.html`)의 `.timeline-card`를
 * React 컴포넌트로 포팅한 뒤, 좌측 색띠 시간 블록을 캐릭터 이미지로 교체한 형태.
 * 일반 카드는 `public/vts/{캐릭터}.png` VTS 전신 일러스트를, 피날레 카드는
 * `public/ch/{캐릭터}.png` SD 캐릭터 일러스트 3인을 살짝 겹쳐 콜라주로 표시한다.
 * 60px 원형 안에서 인물 식별이 쉬운 머리 큰 SD 컷이 콜라주에 더 적합하기 때문.
 *
 * 3열 그리드: [캐릭터 이미지/콜라주] [이름·페르소나·시간] [상태 뱃지]
 *
 * 클릭 동작은 카드 종류에 따라 다르다:
 * - **일반 스트리머 카드** — `<button>`으로 동작하며, 클릭 시 인물 프로필 이미지와
 *   치지직 채널 바로가기를 담은 상세 모달이 열린다.
 * - **합방 피날레 카드** — `<a>` 링크로 동작하며, 모달 없이 합방이 진행되는
 *   호스트 채널(깅나리)로 새 탭에서 바로 이동한다.
 *
 * 시간은 `Asia/Seoul` 기준 `HH:mm – HH:mm` 형식으로 본문 영역에 함께 표기되며,
 * `prefers-reduced-motion` 환경에선 LIVE 뱃지의 펄싱이 자동으로 정지된다.
 */
export function StreamerScheduleCard({
  item,
  status,
}: StreamerScheduleCardProps): React.JSX.Element {
  const isFinale = item.id === 'finale';
  const isPast = status === 'past';
  const [isModalOpen, setIsModalOpen] = useState(false);

  const cardClassName = [
    styles.card,
    isPast ? styles.cardPast : '',
    status === 'live' ? styles.cardLive : '',
    isFinale ? styles.cardFinale : '',
  ]
    .filter(Boolean)
    .join(' ');

  const cardInner = (
    <>
      {isFinale ? (
        <div className={styles.finaleCollage} aria-hidden>
          {STREAMERS.map((streamer) => (
            <img
              key={streamer.id}
              src={streamer.finaleAvatarUrl}
              alt=""
              className={styles.finaleAvatar}
            />
          ))}
        </div>
      ) : (
        <img
          src={item.imageUrl}
          alt={`${item.name} 캐릭터 이미지`}
          className={styles.charImage}
        />
      )}

      <div className={styles.info}>
        <div className={styles.nameRow}>
          <span className={styles.name}>{item.name}</span>
        </div>
        <div className={styles.persona}>{item.persona}</div>
        <div className={styles.format}>
          {formatTime(item.startAt)} – {formatTime(item.endAt)}
        </div>
      </div>

      <StatusBadge status={status} />
    </>
  );

  // 합방 피날레: 외부 링크로 새 탭 이동.
  if (isFinale && item.channelUrl !== null) {
    return (
      <a
        className={cardClassName}
        href={item.channelUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${item.name} 채널로 이동 (새 탭)`}
      >
        {cardInner}
      </a>
    );
  }

  // 일반 스트리머: 버튼으로 동작, 클릭 시 상세 모달 열기.
  return (
    <>
      <button
        type="button"
        className={cardClassName}
        onClick={() => {
          setIsModalOpen(true);
        }}
        aria-label={`${item.name} 상세 정보 열기`}
      >
        {cardInner}
      </button>

      <StreamerDetailModal
        item={isModalOpen ? item : null}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
}

/**
 * 우측 뱃지. 상태에 따라 라벨/색이 달라진다.
 */
function StatusBadge({ status }: { status: ScheduleStatus }): React.JSX.Element {
  switch (status) {
    case 'live':
      return (
        <span className={[styles.badge, styles.badgeLive].join(' ')} aria-label="실시간 방송 중">
          ● 방송 중
        </span>
      );
    case 'next':
      return <span className={[styles.badge, styles.badgeNext].join(' ')}>곧 시작</span>;
    case 'past':
      return <span className={[styles.badge, styles.badgePast].join(' ')}>종료</span>;
    case 'upcoming':
    default:
      return <span className={[styles.badge, styles.badgeUpcoming].join(' ')}>대기</span>;
  }
}

/**
 * ISO 시각을 KST `HH:mm` 형식으로 포맷.
 *
 * 브라우저 로컬 시간이 KST가 아닐 가능성을 대비해 `Asia/Seoul` 타임존을 명시한다.
 */
function formatTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleTimeString('ko-KR', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
